import React, { useEffect, useState, useRef, useCallback } from 'react';
import api from '../../api/axios';
import AdminPageHeader from '../../components/common/AdminPageHeader';
import Toast from '../../components/common/Toast';
import { useMainCompany } from '../../context/MainCompanyContext';

export default function AdminNotifyUsers() {
  const { selectedMain } = useMainCompany();
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [stateId, setStateId] = useState(0);
  const [countyId, setCountyId] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [notifyRecords, setNotifyRecords] = useState([]);
  const [loadingNotify, setLoadingNotify] = useState(false);
  const [selectedNotifyTitle, setSelectedNotifyTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);

  const [selectedCompanies, setSelectedCompanies] = useState(new Set());
  const [toast, setToast] = useState({ message: '', visible: false });
  const selectAllRef = useRef(null);

  // modal schedule
  const [startDT, setStartDT] = useState('');
  const [endDT, setEndDT] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const showToast = (message, duration = 3500) => {
    setToast({ message, visible: true });
    setTimeout(() => setToast({ message: '', visible: false }), duration);
  };

  // load states (same as AdminCompany)
  // load states on mount and when main company changes
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        // clear dependent filters when main changes
        setStateId(0);
        setCountyId(0);
        const params = {};
        if (selectedMain) params.maincompanyid = selectedMain;
        const resp = await api.get('/admin/states', { params });
        const body = resp.data;
        const items = Array.isArray(body) ? body : (Array.isArray(body.data) ? body.data : []);
        if (mounted) setStates(items);
      } catch (e) {
        console.error('Failed to load states', e?.response?.data || e?.message);
        if (mounted) setStates([]);
      }
    };
    load();
    return () => { mounted = false; };
  }, [selectedMain]);

  // when states load, default-select California if present and stateId not set
  useEffect(() => {
    if (states && states.length && !stateId) {
      const ca = states.find(s => (s.statename || s.name || '').toLowerCase() === 'california');
      if (ca && (ca.id || ca.stateid || ca.value)) {
        setStateId(ca.id || ca.stateid || ca.value);
      }
    }
  }, [states, stateId]);

  // inject same select CSS to ensure options are visible
  useEffect(() => {
    const css = `.admin-company-page select, .admin-company-page select option { color: #000 !important; background: #fff !important; }`;
    const style = document.createElement('style');
    style.setAttribute('data-generated', 'admin-company-select');
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
    return () => { try { document.head.removeChild(style); } catch (e) {} };
  }, []);

  // load counties when stateId changes
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!stateId) return setCounties([]);
      try {
        const params = { stateId };
        if (selectedMain) params.maincompanyid = selectedMain;
        const resp = await api.get('/auth/counties', { params });
        const body = resp.data;
        const items = Array.isArray(body) ? body : (Array.isArray(body.data) ? body.data : []);
        if (mounted) setCounties(items);
      } catch (e) {
        console.error('Failed to load counties', e?.response?.data || e?.message);
        if (mounted) setCounties([]);
      }
    };
    load();
    return () => { mounted = false; };
  }, [stateId, selectedMain]);

  // fetch companies using same API as AdminCompany
  const fetchCompanies = useCallback(async (tentativePage = null, tentativeLimit = null) => {
    setLoading(true);
    try {
      const params = {};
      if (stateId) params.stateid = stateId;
      if (countyId) params.countyid = countyId;
      const reqPage = Number(tentativePage ?? page) || 1;
      const reqLimit = Number(tentativeLimit ?? limit) || 10;
      params.page = reqPage;
      params.limit = reqLimit;

      const resp = await api.get(`/admin/sub-companies/${selectedMain}`, { params });
      const body = resp.data;
      let items = [];
      let meta = { total: 0, page: reqPage, limit: reqLimit };
      if (body && body.data && body.data.data && Array.isArray(body.data.data)) {
        items = body.data.data;
        if (body.data.meta) meta = { ...meta, ...body.data.meta };
      } else if (body && Array.isArray(body.data)) {
        items = body.data;
        meta = { total: items.length, page: reqPage, limit: reqLimit };
      } else if (Array.isArray(body)) {
        items = body;
        meta = { total: items.length, page: reqPage, limit: reqLimit };
      }
      setCompanies(items);
      setTotal(Number(meta.total) || 0);
      setPage(Number(meta.page) || reqPage);
      setLimit(Number(meta.limit) || reqLimit);
      return { success: true, meta };
    } catch (e) {
      console.error('Error fetching sub-companies', e?.response?.status, e?.response?.data || e.message);
      setCompanies([]);
      const msg = e?.response?.data?.message || e?.response?.data || e?.message || 'Failed to load data';
      showToast(msg);
      return { success: false, error: e };
    } finally {
      setLoading(false);
    }
  }, [selectedMain, stateId, countyId, page, limit]);

  // Debounced fetch: fetch companies and notify records once when selectedMain/stateId/countyId change
  const fetchTimerRef = React.useRef(null);
  const fetchNotifyRecords = useCallback(async () => {
    setLoadingNotify(true);
    try {
      const params = {};
      if (stateId) params.stateid = stateId;
      if (countyId) params.countyid = countyId;
      const resp = await api.get('/admin/notify-user', { params });
      // API returns an array
      const items = Array.isArray(resp.data) ? resp.data : (Array.isArray(resp.data && resp.data.data) ? resp.data.data : []);
      setNotifyRecords(items || []);
    } catch (e) {
      console.error('Failed to load notify records', e?.response?.data || e?.message);
      setNotifyRecords([]);
      setSelectedNotifyTitle('');
    } finally {
      setLoadingNotify(false);
    }
  }, [stateId, countyId]);

  useEffect(() => {
    if (!selectedMain) return;
    // clear any in-flight timer
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = setTimeout(async () => {
      // clear selected companies when filters change
      setSelectedCompanies(new Set());
      // also clear selected notify title and reset date fields for safety
      setSelectedNotifyTitle('');
      setStartDT('');
      setEndDT('');
      setErrors({});
      try {
        await fetchCompanies(1, limit);
        await fetchNotifyRecords();
      } catch (e) {
        // errors are handled inside fetch functions
      }
    }, 200);

    return () => {
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
    };
  }, [selectedMain, stateId, countyId, fetchCompanies, fetchNotifyRecords, limit]);

  // Fetch notify-user records for current state/county filters
  // ...fetchNotifyRecords is defined above with useCallback

  // convert SQL/ISO datetime string to datetime-local input value (local time)
  const sqlToLocalInput = (dt) => {
    if (!dt) return '';
    let d = new Date(dt);
    if (isNaN(d.getTime())) {
      // try replace space with T
      d = new Date(String(dt).replace(' ', 'T'));
      if (isNaN(d.getTime())) return '';
    }
    const pad = (n) => String(n).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    const ss = pad(d.getSeconds());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
  };

  // When state changes we need to clear selected companies and fetch appropriate companies
  const handleStateChange = async (e) => {
    const newStateId = Number(e.target.value) || 0;
    setStateId(newStateId);
    // clear county selection when state changes
    setCountyId(0);
    // clear selected companies; fetching is handled by the debounced effect
    setSelectedCompanies(new Set());
    // deselect any selected notify title and reset date fields to prevent accidental rescheduling
    setSelectedNotifyTitle('');
    setStartDT('');
    setEndDT('');
    setErrors({});
  };

  const handleCountyChange = async (e) => {
    const newCountyId = Number(e.target.value) || 0;
    setCountyId(newCountyId);
    // clear selected companies; fetching is handled by the debounced effect
    setSelectedCompanies(new Set());
    // deselect any selected notify title and reset date fields to prevent accidental rescheduling
    setSelectedNotifyTitle('');
    setStartDT('');
    setEndDT('');
    setErrors({});
  };

  // Whenever companies or notifyRecords change, apply pre-selection of companies
  useEffect(() => {
    if (!companies || !companies.length || !notifyRecords || !notifyRecords.length) return;
    const companyIdsFromNotify = new Set(notifyRecords.map(r => Number(r.companyid)).filter(x => !isNaN(x) && x > 0));
    const toSelect = new Set();
    for (const c of companies) {
      const cid = Number(c.id || c.company_id || c.companyid);
      if (!isNaN(cid) && companyIdsFromNotify.has(cid)) toSelect.add(cid);
    }
    if (toSelect.size) setSelectedCompanies(toSelect);
  }, [companies, notifyRecords]);

  const toggleCompany = (id) => {
    const s = new Set(selectedCompanies);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedCompanies(s);
  };

  const toggleSelectAll = (checked) => {
    if (!checked) {
      setSelectedCompanies(new Set());
      return;
    }
    // select all visible companies
    const s = new Set();
    for (const c of companies) {
      const cid = Number(c.id || c.company_id || c.companyid);
      if (!isNaN(cid) && cid > 0) s.add(cid);
    }
    setSelectedCompanies(s);
  };

  // manage indeterminate state on the select-all checkbox
  useEffect(() => {
    const el = selectAllRef.current;
    if (!el) return;
    const total = companies ? companies.length : 0;
    const selected = selectedCompanies ? selectedCompanies.size : 0;
    el.indeterminate = selected > 0 && selected < total;
  }, [companies, selectedCompanies]);

  const canNotify = selectedCompanies.size > 0;

  // resetForm removed (unused)

  // format a datetime-local string (YYYY-MM-DDTHH:mm or with seconds) to "YYYY-MM-DD HH:mm:ss"
  const formatDateTimeLocalToSQL = (dtLocal) => {
    if (!dtLocal) return null;
    // dtLocal is like '2025-09-11T09:00' or '2025-09-11T09:00:00'
    const d = new Date(dtLocal);
    if (isNaN(d.getTime())) return null;
    const pad = (n) => String(n).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    const ss = pad(d.getSeconds());
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
  };

  // submitSchedule removed (logic handled inline in handlers)

  return (
    <div className="p-4 admin-company-page">
      <Toast message={toast.message} visible={toast.visible} />

      <AdminPageHeader
        title="Notify Users"
        filters={(
          <div className="flex gap-2 items-center">
            <select value={stateId} onChange={(e) => handleStateChange(e)} className="border px-2 py-2 rounded text-sm bg-white text-black w-56">
              <option value={0}>Select State</option>
              {states.map((s) => (
                <option key={s.id || s.stateid || s.value} value={s.id || s.stateid || s.value}>{s.statename || s.name}</option>
              ))}
            </select>
            <select value={countyId} onChange={(e) => handleCountyChange(e)} className="border px-2 py-2 rounded text-sm bg-white text-black w-56">
              <option value={0}>All Counties</option>
              {counties.map((c) => (
                <option key={c.id || c.countyid || c.value} value={c.id || c.countyid || c.value}>{c.countyname || c.name}</option>
              ))}
            </select>
          </div>
        )}
      />

      <div className="bg-white rounded shadow overflow-hidden">
        <div className="flex flex-col md:flex-row">
          {/* Left: companies list */}
          <div className="w-full md:w-1/2 p-4 border-r md:border-r-2">
            <h3 className="text-lg font-medium mb-3">Companies</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="px-2 py-2 text-left">
                      <input ref={selectAllRef} type="checkbox" checked={selectedCompanies.size > 0 && selectedCompanies.size === companies.length} onChange={(e) => toggleSelectAll(e.target.checked)} />
                    </th>
                    <th className="px-2 py-2 text-left">Company</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="2" className="text-center py-4 text-gray-500">Loading...</td></tr>
                  ) : companies.length > 0 ? (
                    companies.map((c) => (
                      <tr key={c.id} className="border-t hover:bg-gray-50">
                        <td className="px-2 py-2">
                          {(() => {
                            const cid = Number(c.id || c.company_id || c.companyid);
                            return (
                              <input type="checkbox" checked={selectedCompanies.has(cid)} onChange={() => toggleCompany(cid)} />
                            );
                          })()}
                        </td>
                        <td className="px-2 py-2">{c.company_name || c.name || c.company}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="2" className="text-center py-4 text-gray-500">No results found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right: scheduling form */}
          <div className="w-full md:w-1/2 p-4">
            <h3 className="text-lg font-medium mb-3">Schedule Notification</h3>
            <div>
              {/* Notification title removed - generated on backend */}
              {errors.main && <div className="text-red-600 text-sm mb-2">{errors.main}</div>}
              {errors.state && <div className="text-red-600 text-sm mb-2">{errors.state}</div>}

              {/* show unique fetched notify titles as clickable links */}
              <label className="block text-sm font-medium text-gray-700">Notify Events</label>
              <div className="mt-1">
                  {loadingNotify ? (
                  <div className="text-sm text-gray-500">Loading notify events...</div>
                ) : notifyRecords && notifyRecords.length ? (
                  (() => {
                    // extract unique notify_title values in order
                    const seen = new Set();
                    const uniques = [];
                    for (const r of notifyRecords) {
                      const t = r.notify_title || '';
                      if (t && !seen.has(t)) { seen.add(t); uniques.push(t); }
                    }
                    return (
                      <div className="flex flex-wrap gap-2">
                        {uniques.map((t) => (
                          <button key={t} type="button" onClick={() => {
                                  // toggle selection: deselect if clicking the currently selected title
                                  if (selectedNotifyTitle === t) {
                                    // deselect
                                    setSelectedNotifyTitle('');
                                    // reset datetime fields and errors
                                    setStartDT('');
                                    setEndDT('');
                                    setErrors({});
                                    // visual feedback provided by empty fields and disabled buttons
                                    return;
                                  }
                                  setSelectedNotifyTitle(t);
                                  // find records matching this title
                                  const matches = notifyRecords.filter(rr => rr.notify_title === t);
                                  // pre-select companies corresponding to these records
                                  const toSelect = new Set(matches.map(m => Number(m.companyid)).filter(x => !isNaN(x) && x > 0));
                                  if (toSelect.size) setSelectedCompanies(toSelect);
                                  // set start/end from first match
                                  const match = matches[0];
                                  if (match) {
                                    setStartDT(sqlToLocalInput(match.notify_start));
                                    setEndDT(sqlToLocalInput(match.notify_end));
                                  }
                                }} className={`text-sm px-2 py-1 rounded ${selectedNotifyTitle === t ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}>
                                  {t}
                                </button>
                        ))}
                      </div>
                    );
                  })()
                ) : (
                  <div className="text-sm text-gray-500">No notify events</div>
                )}
              </div>

              <label className="block text-sm font-medium text-gray-700 mt-3">Start Date-Time</label>
              <input type="datetime-local" value={startDT} onChange={e => setStartDT(e.target.value)} className="mt-1 block w-full rounded border px-2 py-1" />
              {errors.start && <div className="text-red-600 text-sm mt-1">{errors.start}</div>}

              <label className="block text-sm font-medium text-gray-700 mt-3">End Date-Time</label>
              <input type="datetime-local" value={endDT} onChange={e => setEndDT(e.target.value)} className="mt-1 block w-full rounded border px-2 py-1" />
              {errors.end && <div className="text-red-600 text-sm mt-1">{errors.end}</div>}
              {errors.order && <div className="text-red-600 text-sm mt-1">{errors.order}</div>}

              <div className="mt-4 text-right flex gap-2 justify-end items-center">
                {/* Add New: visible by default. Per requirement, disabled when no notify title is selected (and while submitting) */}
                <button type="button" disabled={submitting || !canNotify} onClick={async () => {
                  // create new notification (POST)
                  if (submitting) return;
                  setSubmitting(true);
                  try {
                    // validate basics
                    const err = {};
                    if (!startDT) err.start = 'Start date-time required';
                    if (!endDT) err.end = 'End date-time required';
                    if (!selectedCompanies || selectedCompanies.size === 0) err.companies = 'Select at least one company';
                    if (startDT && endDT && new Date(startDT) >= new Date(endDT)) err.order = 'Start must be before End';
                    setErrors(err);
                    if (Object.keys(err).length) return;

                    const companyIds = Array.from(selectedCompanies.values()).map(x => Number(x));
                    let inferredCountyId = Number(countyId) || null;
                    if (!inferredCountyId && companyIds && companyIds.length) {
                      const selectedCompanyObjects = companies.filter(c => companyIds.includes(Number(c.id || c.company_id || c.companyid)));
                      const countyVals = selectedCompanyObjects.map(c => Number(c.countyid || c.county || c.county_id || c.countyId || 0)).filter(x => !isNaN(x) && x > 0);
                      const uniqueCounties = Array.from(new Set(countyVals));
                      if (uniqueCounties.length === 1) inferredCountyId = uniqueCounties[0];
                      else if (companyIds.length === 1 && uniqueCounties.length === 0) {
                        const c = selectedCompanyObjects[0];
                        const raw = c && (c.countyid || c.county || c.county_id || c.countyId);
                        inferredCountyId = raw ? Number(raw) : null;
                      }
                    }

                    const payload = {
                      maincompanyid: Number(selectedMain),
                      companyid: companyIds,
                      stateid: Number(stateId) || null,
                      countyid: inferredCountyId,
                      notify_start: formatDateTimeLocalToSQL(startDT),
                      notify_end: formatDateTimeLocalToSQL(endDT)
                    };

                    const resp = await api.post('/admin/notify-user', payload);
                    showToast('Notification added successfully!');
                    // refresh notify records so the new title appears
                    await fetchNotifyRecords();
                    // if server returned a notify_title, select it
                    const returned = resp.data && resp.data.data ? resp.data.data : resp.data;
                    const returnedTitle = returned && returned.notify_title ? returned.notify_title : (Array.isArray(returned) && returned[0] && returned[0].notify_title ? returned[0].notify_title : null);
                    if (returnedTitle) setSelectedNotifyTitle(returnedTitle);
                  } catch (err) {
                    const msg = err?.response?.data?.message || err?.message || 'Failed to add notification';
                    showToast(msg);
                  } finally {
                    setSubmitting(false);
                  }
                }} className={`px-4 py-2 rounded text-white ${(!canNotify || submitting) ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}>
                  {submitting ? 'Processing...' : 'Add New'}
                </button>

                {/* Update: only visible when a notify title is selected */}
                {selectedNotifyTitle ? (
                  <button type="button" disabled={submitting} onClick={async () => {
                    // update existing notification (PUT)
                    if (submitting) return;
                    const ok = window.confirm('Are you sure you want to update (re-schedule) this notification? This will replace existing entries for this notify title.');
                    if (!ok) return;
                    setSubmitting(true);
                    try {
                      const err = {};
                      if (!startDT) err.start = 'Start date-time required';
                      if (!endDT) err.end = 'End date-time required';
                      if (!selectedCompanies || selectedCompanies.size === 0) err.companies = 'Select at least one company';
                      if (startDT && endDT && new Date(startDT) >= new Date(endDT)) err.order = 'Start must be before End';
                      setErrors(err);
                      if (Object.keys(err).length) return;

                      const companyIds = Array.from(selectedCompanies.values()).map(x => Number(x));
                      let inferredCountyId = Number(countyId) || null;
                      if (!inferredCountyId && companyIds && companyIds.length) {
                        const selectedCompanyObjects = companies.filter(c => companyIds.includes(Number(c.id || c.company_id || c.companyid)));
                        const countyVals = selectedCompanyObjects.map(c => Number(c.countyid || c.county || c.county_id || c.countyId || 0)).filter(x => !isNaN(x) && x > 0);
                        const uniqueCounties = Array.from(new Set(countyVals));
                        if (uniqueCounties.length === 1) inferredCountyId = uniqueCounties[0];
                        else if (companyIds.length === 1 && uniqueCounties.length === 0) {
                          const c = selectedCompanyObjects[0];
                          const raw = c && (c.countyid || c.county || c.county_id || c.countyId);
                          inferredCountyId = raw ? Number(raw) : null;
                        }
                      }

                      const payload = {
                        notify_title: selectedNotifyTitle,
                        maincompanyid: Number(selectedMain),
                        companyid: companyIds,
                        stateid: Number(stateId) || null,
                        countyid: inferredCountyId,
                        notify_start: formatDateTimeLocalToSQL(startDT),
                        notify_end: formatDateTimeLocalToSQL(endDT)
                      };

                      await api.put('/admin/notify-user', payload);
                      showToast('Notification updated successfully!');
                      await fetchNotifyRecords();
                    } catch (err) {
                      const msg = err?.response?.data?.message || err?.message || 'Failed to update notification';
                      showToast(msg);
                    } finally {
                      setSubmitting(false);
                    }
                  }} className={`px-4 py-2 rounded text-white ${submitting ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}>
                    {submitting ? 'Processing...' : 'Update'}
                  </button>
                ) : null}

                {/* Delete: visible when a notify title is selected */}
                {selectedNotifyTitle ? (
                  <button type="button" disabled={submitting} onClick={async () => {
                    const ok = window.confirm('Are you sure you want to DELETE this notify title and all associated records? This action cannot be undone.');
                    if (!ok) return;
                    setSubmitting(true);
                    try {
                      await api.delete(`/admin/notify-user/${encodeURIComponent(selectedNotifyTitle)}`);
                      showToast('Notify title deleted successfully!');
                      // clear selection and form
                      setSelectedNotifyTitle('');
                      setSelectedCompanies(new Set());
                      setStartDT('');
                      setEndDT('');
                      setErrors({});
                      // refresh data
                      await fetchCompanies(1, limit);
                      await fetchNotifyRecords();
                    } catch (err) {
                      const msg = err?.response?.data?.message || err?.message || 'Failed to delete notify title';
                      showToast(msg);
                    } finally {
                      setSubmitting(false);
                    }
                  }} className={`px-4 py-2 rounded text-white ${submitting ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'}`}>
                    {submitting ? 'Processing...' : 'Delete'}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pagination (simple) */}
      {Math.ceil((total || companies.length) / limit) > 1 && (
        <div className="flex justify-between items-center mt-3 text-sm">
          <span>Showing {(page - 1) * limit + 1}–{Math.min((page) * limit, total || companies.length)} of {total || companies.length}</span>
          <div className="flex gap-2 items-center">
            <label>Rows</label>
            <select value={limit} onChange={async (e) => { const newLimit = Number(e.target.value) || 10; const resp = await fetchCompanies(1, newLimit); if (resp && resp.success) {} }} className="border rounded px-2 py-1">
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <button onClick={async () => { const target = Math.max(1, page - 1); await fetchCompanies(target); }} disabled={page === 1} className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100">Prev</button>
            <span>{page} / {Math.ceil((total || companies.length) / limit)}</span>
            <button onClick={async () => { const last = Math.ceil((total || companies.length) / limit) || 1; const target = Math.min(last, page + 1); await fetchCompanies(target); }} disabled={page >= Math.ceil((total || companies.length) / limit)} className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100">Next</button>
          </div>
        </div>
      )}

  {/* modal removed; scheduling form is inline */}

    </div>
  );
}
